import * as React from 'react'
import { ReactElement } from 'react'
import { makeStyles, Text, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import EditableDate from '../../components/Date/EditableDate'
import { DateFormats, formatIsoDate, parseDate } from '../../helpers/dateHelper'
import {
    applyTaskDateShift,
    getVisibleTaskDates,
    ResolvedTaskDateConfig,
    TaskDates as TaskDatesValue,
    TaskDateName,
} from '../../helpers/taskDateHelper'

const useStyles = makeStyles({
    // Side by side, never underneath each other: two dates may make a row wider, but not taller (HAM-4286).
    dates: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXS,
        minWidth: 0,
    },
    separator: { color: tokens.colorNeutralForeground3 },
})

export type TaskDatesProps = {
    dates: TaskDatesValue
    config: ResolvedTaskDateConfig
    /** The short notation for narrow screens; the tooltip always spells the date out in full. */
    compact?: boolean
    setDates: (dates: TaskDatesValue) => void
}

/**
 * The dates of a task in an overview. Which dates are shown follows the office setting: a single date, or the start
 * and the due date next to each other. Every date shown can be changed on the spot, and changing one moves the other
 * along exactly like the task form does. An empty date shows nothing at all and cannot be filled in from here - that
 * is what the task form is for (HAM-4286).
 */
export default function TaskDates(props: TaskDatesProps): ReactElement {
    const styles = useStyles()
    const { i18n } = useTranslation()

    const setDate = (field: TaskDateName, date: Date) => props.setDates(applyTaskDateShift(props.dates, field, formatIsoDate(date)))

    const visible = getVisibleTaskDates(props.config).filter(name => props.dates[name])

    return (
        <div className={styles.dates}>
            {visible.map((name, index) => (
                <React.Fragment key={name}>
                    {index > 0 && (
                        <Text size={200} className={styles.separator}>
                            {'-'}
                        </Text>
                    )}
                    <EditableDate
                        date={props.dates[name]}
                        format={props.compact ? DateFormats.Compact : undefined}
                        tooltip={parseDate(props.dates[name], { locale: i18n.language })?.toFormat(DateFormats.Full)}
                        setDate={date => setDate(name, date)}
                    />
                </React.Fragment>
            ))}
        </div>
    )
}
